/**
 * Product Gallery Component with Splide Integration
 * 
 * Features:
 * - Variant-driven image sets
 * - Fullscreen zoom gallery with vertical scrolling
 * - Syncs fullscreen gallery to current slide of main gallery
 * - Embedded video support (Vimeo with custom controls, YouTube, Shopify)
 * - Keyboard and mouse wheel navigation
 */

(function () {
  'use strict';

  class ProductGallery {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.productContainer = wrapper.closest('[data-section-type="product"]') || wrapper.closest('[data-section-id]');

      // Track VimeoPlayerComponent instances for cleanup
      this.vimeoPlayers = new Map();

      // Set total media count FIRST (needed for parsing the map)
      this.totalMedia = parseInt(wrapper.dataset.totalMedia, 10) || 0;

      // Parse variant media mapping from data attribute
      // Format: "variantId1:mediaPosition1,variantId2:mediaPosition2,..."
      this.variantMediaMap = this.parseVariantMediaMap(wrapper.dataset.variantMediaMap || '');
      this.currentVariantId = wrapper.dataset.currentVariantId || null;


      // Get slider elements
      this.thumbSlider = wrapper.querySelector('[data-product-thumbs]');
      this.mainSlider = wrapper.querySelector('[data-product-photos]');

      if (!this.thumbSlider || !this.mainSlider) {
        console.error('ProductGallery: Required slider elements not found');
        return;
      }

      // Splide instances
      this.thumbSplide = null;
      this.mainSplide = null;
      this.fullscreenSplide = null;

      // Fullscreen elements
      this.fullscreenModal = document.querySelector('[data-gallery-fullscreen]');
      this.fullscreenSlider = document.querySelector('[data-gallery-fullscreen-slider]');
      this.zoomButton = wrapper.querySelector('[data-gallery-zoom-trigger]');
      this.closeButton = document.querySelector('[data-gallery-close]');

      // Video fullscreen modal elements
      this.videoFullscreenModal = document.querySelector('[data-video-fullscreen-modal]');
      this.videoFullscreenContent = document.querySelector('[data-video-fullscreen-content]');
      this.videoFullscreenClose = document.querySelector('[data-video-fullscreen-close]');

      // Cache setup
      this.thumbCache = [];
      this.mainCache = [];
      this.fullscreenCache = [];

      this.setupCaches();
      this.preventSlick();

      // On initial load, filter slides if we have variant media mapping
      let initialStartIndex = 0;
      if (this.variantMediaMap.size > 0 && this.currentVariantId) {
        // Build slides for the current variant on initial load
        initialStartIndex = this.buildSlides(this.currentVariantId, null);
      } else {
        // Find initial starting index from the starting-slide marker
        initialStartIndex = this.getInitialStartIndex();
      }

      this.mountSplide(initialStartIndex);
      this.setupVariantListener();
      this.setupFullscreenGallery();
      this.setupVideoFullscreenModal();

      // Track pending variant updates to prevent stacking
      this._pendingVariantUpdate = null;

      // Expose instance globally
      window.productGalleryInstance = this;
      this.wrapper._productGalleryInstance = this;

      // Dispatch ready event
      this.wrapper.dispatchEvent(new CustomEvent('productGallery:ready', {
        bubbles: true,
        detail: { instance: this }
      }));
    }

    /**
     * RequestIdleCallback polyfill for better performance scheduling
     */
    _scheduleIdleTask(callback) {
      if (typeof requestIdleCallback !== 'undefined') {
        return requestIdleCallback(callback, { timeout: 100 });
      }
      // Fallback to requestAnimationFrame with timeout
      return requestAnimationFrame(() => {
        const start = Date.now();
        callback({
          didTimeout: false,
          timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
        });
      });
    }

    /**
     * Parse variant media map string into structured data
     * Format: "variantId1:mediaPosition1,variantId2:mediaPosition2,..."
     * Returns: Map of variantId -> { startIndex, endIndex }
     */
    parseVariantMediaMap(mapString) {
      if (!mapString) return new Map();

      const map = new Map();
      const entries = mapString.split(',').filter(Boolean);

      // Parse all variant -> media position entries
      const variantMediaPositions = [];
      for (const entry of entries) {
        const [variantId, position] = entry.split(':');
        if (variantId && position !== undefined) {
          variantMediaPositions.push({
            variantId: variantId.trim(),
            position: parseInt(position, 10)
          });
        }
      }

      // Sort by position to ensure correct ordering
      variantMediaPositions.sort((a, b) => a.position - b.position);

      // Calculate ranges for each variant
      // Logic: images from variant's position to next variant's position (exclusive) belong to this variant
      for (let i = 0; i < variantMediaPositions.length; i++) {
        const current = variantMediaPositions[i];
        const next = variantMediaPositions[i + 1];

        map.set(current.variantId, {
          startIndex: current.position,
          endIndex: next ? next.position : this.totalMedia // to the end if last variant
        });
      }

      return map;
    }

    /**
     * Setup listener for variant changes from theme.Product
     */
    setupVariantListener() {
      if (!this.productContainer) {
        console.warn('⚠️ ProductGallery: No product container found - variant updates may not work');
        return;
      }

      // Listen for the variantChange event from theme.js
      this.productContainer.addEventListener('variantChange', (evt) => {
        const variant = evt.detail?.variant;
        if (variant) {
          this.renderVariant(variant);
        }
      });
    }

    /**
     * Cache all slides for both carousels
     * Captures the original HTML before Splide modifies it
     */
    setupCaches() {
      // Cache thumbnails
      const thumbSlides = this.thumbSlider.querySelectorAll('[data-gallery-thumb-slide]');
      thumbSlides.forEach((slide, index) => {
        this.thumbCache.push({
          html: slide.outerHTML,
          mediaId: slide.dataset.mediaId,
          index: parseInt(slide.dataset.index, 10)
        });
      });

      // Cache main slides
      const mainSlides = this.mainSlider.querySelectorAll('[data-gallery-main-slide]');
      mainSlides.forEach((slide, index) => {
        this.mainCache.push({
          html: slide.outerHTML,
          mediaId: slide.dataset.mediaId,
          index: parseInt(slide.dataset.index, 10)
        });
      });

      // Cache fullscreen slides if modal exists
      if (this.fullscreenSlider) {
        const fullscreenSlides = this.fullscreenSlider.querySelectorAll('[data-gallery-fullscreen-slide]');
        fullscreenSlides.forEach((slide, index) => {
          this.fullscreenCache.push({
            html: slide.outerHTML,
            mediaId: slide.dataset.mediaId,
            mediaType: slide.dataset.mediaType,
            index: parseInt(slide.dataset.index, 10)
          });
        });
      }
    }

    /**
     * Build slides filtered by variant's image range
     * Uses DocumentFragment for efficient DOM updates
     * @param {string} variantId - The variant ID to show images for
     * @param {string} featuredMediaId - The featured media ID to start on
     */
    buildSlides(variantId, featuredMediaId) {
      // Get the image range for this variant
      const range = this.variantMediaMap.get(String(variantId));

      let filteredThumbs = this.thumbCache;
      let filteredMain = this.mainCache;
      let filteredFullscreen = this.fullscreenCache;

      if (range) {
        // Filter by index range
        filteredThumbs = this.thumbCache.filter(item =>
          item.index >= range.startIndex && item.index < range.endIndex
        );
        filteredMain = this.mainCache.filter(item =>
          item.index >= range.startIndex && item.index < range.endIndex
        );
        filteredFullscreen = this.fullscreenCache.filter(item =>
          item.index >= range.startIndex && item.index < range.endIndex
        );
      }

      // If no matches, show all
      if (filteredThumbs.length === 0) {
        filteredThumbs = this.thumbCache;
        filteredMain = this.mainCache;
        filteredFullscreen = this.fullscreenCache;
      }

      // Use DocumentFragment for efficient DOM updates (single reflow)
      const thumbList = this.thumbSlider.querySelector('.splide__list');
      const thumbFragment = document.createDocumentFragment();
      const tempThumbDiv = document.createElement('div');
      tempThumbDiv.innerHTML = filteredThumbs.map(item => item.html).join('');
      while (tempThumbDiv.firstChild) {
        thumbFragment.appendChild(tempThumbDiv.firstChild);
      }
      thumbList.innerHTML = '';
      thumbList.appendChild(thumbFragment);

      // Rebuild main list with DocumentFragment
      const mainList = this.mainSlider.querySelector('.splide__list');
      const mainFragment = document.createDocumentFragment();
      const tempMainDiv = document.createElement('div');
      tempMainDiv.innerHTML = filteredMain.map(item => item.html).join('');
      while (tempMainDiv.firstChild) {
        mainFragment.appendChild(tempMainDiv.firstChild);
      }
      mainList.innerHTML = '';
      mainList.appendChild(mainFragment);

      // Ensure images have correct CSS classes for LQIP blur
      this.normalizeSlideImages(mainList);

      // Store fullscreen data for lazy rebuild (defer until needed)
      this._pendingFullscreenSlides = filteredFullscreen;

      // Find starting index based on featured media
      let startIndex = 0;
      if (featuredMediaId) {
        const foundIndex = filteredMain.findIndex(item =>
          item.mediaId === String(featuredMediaId)
        );
        if (foundIndex !== -1) {
          startIndex = foundIndex;
        }
      }

      return startIndex;
    }

    /**
     * Mount or remount Splide instances
     * @param {number} startIndex - Starting slide index
     * @returns {Promise} Resolves when mounting is complete
     */
    mountSplide(startIndex = 0) {
      return new Promise((resolve) => {
        // Disconnect resize observer before destroying sliders
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }

        // Destroy existing instances properly
        if (this.thumbSplide) {
          try {
            this.thumbSplide.destroy();
          } catch (e) {
            console.warn('Error destroying thumb splide:', e);
          }
          this.thumbSplide = null;
        }
        if (this.mainSplide) {
          try {
            this.mainSplide.destroy();
          } catch (e) {
            console.warn('Error destroying main splide:', e);
          }
          this.mainSplide = null;
        }

        // Check if Splide is available
        if (typeof Splide === 'undefined') {
          console.error('Splide library not loaded');
          resolve();
          return;
        }

        // Use requestAnimationFrame for synchronous DOM update instead of setTimeout
        requestAnimationFrame(() => {
          try {

            // Get slide counts
            const thumbSlideCount = this.thumbSlider.querySelectorAll('.splide__slide').length;
            const mainSlideCount = this.mainSlider.querySelectorAll('.splide__slide').length;

            // Early return if no slides - can't initialize Splide without slides
            if (mainSlideCount === 0) {
              console.warn('⚠️ No slides found in main slider, skipping initialization');
              this.wrapper.classList.add('is-initialized');
              return;
            }

            // Determine thumbnail direction based on data-position attribute
            const thumbPosition = this.thumbSlider.dataset.position || 'beside';
            const isHorizontal = thumbPosition === 'below';

            // Initialize thumbnail slider
            this.thumbSplide = new Splide(this.thumbSlider, {
              type: 'loop',
              direction: isHorizontal ? 'ltr' : 'ttb',
              height: 'auto',
              perMove: 1,
              gap: 6,
              rewind: false,
              pagination: false,
              arrows: true,
              isNavigation: true,
              focus: 1,
              trimSpace: true,
              fixedWidth: isHorizontal ? false : 84,
              fixedHeight: isHorizontal ? false : 84,
              perPage: isHorizontal ? 6 : undefined,
              start: startIndex,
              flickPower: 300,
              flickMaxPages: 1,
              dragMinThreshold: 10,
              breakpoints: {
                1100: {
                  direction: 'ltr',
                  height: 'auto',
                  perPage: 6,
                  fixedWidth: false,
                  fixedHeight: false
                }
              }
            });

            // Setup Vimeo video handlers for main slider
            this.setupMainSliderVimeoHandlers();

            // Initialize main slider
            // Note: We disable Splide's lazy loading and handle it ourselves with LQIP
            this.mainSplide = new Splide(this.mainSlider, {
              type: 'loop',
              rewind: false,
              pagination: false,
              arrows: false,
              start: startIndex,
              lazyLoad: false,
              flickPower: 300,
              flickMaxPages: 1,
              dragMinThreshold: 10,
              speed: 600,
              easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
            });

            // Track previous index to detect actual slide changes
            let previousMainIndex = startIndex;

            // Track main slider changes for media handling
            this.mainSplide.on('moved', (newIndex) => {
              this._lastMainSliderIndex = newIndex;

              // Only handle media changes if we actually changed slides
              if (newIndex !== previousMainIndex) {
                // Reset video on old slide to poster state
                const oldSlide = this.getSlideElement(previousMainIndex);
                if (oldSlide) {
                  this.stopMediaOnSlide(oldSlide);
                  this.resetVideoOnSlide(oldSlide);
                }

                // Prepare media on new slide
                const newSlide = this.getSlideElement(newIndex);
                if (newSlide) {
                  this.prepMediaOnSlide(newSlide);
                }

                // Update previous index
                previousMainIndex = newIndex;
              }
            });

            // Setup mounted event handler for lazy loading
            this.mainSplide.on('mounted', () => {
              // Get the actual current index after Splide has positioned everything
              const currentIndex = this.mainSplide.index;
              this._lastMainSliderIndex = currentIndex;

              // Initialize lazy loading now that Splide is fully ready
              this.initMainSliderLQIP(currentIndex);

              // Prepare media on the initial slide
              const initialSlide = this.getSlideElement(currentIndex);
              if (initialSlide) {
                this.prepMediaOnSlide(initialSlide);
              }
            });

            // Handle drag events to disable iframe pointer events during drag
            this.mainSplide.on('drag', () => {
              // Disable pointer events on iframes during drag so slider can be dragged
              const iframes = this.mainSlider.querySelectorAll('iframe');
              iframes.forEach(iframe => iframe.style.pointerEvents = 'none');
            });

            this.mainSplide.on('dragged', () => {
              // Re-enable pointer events on iframes after drag
              const iframes = this.mainSlider.querySelectorAll('iframe');
              iframes.forEach(iframe => iframe.style.pointerEvents = '');
            });

            // Sync sliders BEFORE mounting - this is required for Splide sync to work
            // Only sync if both sliders have more than 1 slide to prevent sync errors
            const shouldSync = thumbSlideCount > 1 && mainSlideCount > 1;
            if (shouldSync) {
              try {
                this.mainSplide.sync(this.thumbSplide);
              } catch (syncError) {
                console.warn('⚠️ Could not sync sliders:', syncError.message);
              }
            } else {
            }

            // Mount both sliders with error handling
            // IMPORTANT: Mount main slider FIRST when synced, as thumb slider will try to communicate with it
            try {
              this.mainSplide.mount();
            } catch (mainMountError) {
              console.warn('⚠️ Error mounting main slider:', mainMountError.message);
            }

            try {
              this.thumbSplide.mount();
            } catch (thumbMountError) {
              console.warn('⚠️ Error mounting thumb slider:', thumbMountError.message);
            }

            // Setup listener to disable slider dragging when Vimeo controls are being used
            this.setupVimeoControlInteraction();

            // Check if all thumbs fit and disable thumb slider if so
            this.checkAndDisableThumbSlider();


            // Mark gallery as initialized (removes skeleton, shows content)
            this.wrapper.classList.add('is-initialized');

            // Setup keyboard navigation for thumbnails and main slider
            this.setupThumbnailKeyboardNav();
            this.setupMainSliderKeyboardNav();

            // Initialize lazy loading for thumbnails
            this.initThumbLazyLoad();

            // Note: Main slider LQIP initialization moved to 'mounted' event handler above

            // Set initial thumbnail height and re-check thumb slider
            this.matchThumbnailHeight();

            // Use ResizeObserver for instant updates (no debounce lag)
            if (!this._resizeObserver) {
              this._resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(() => this.matchThumbnailHeight());
              });
              this._resizeObserver.observe(this.mainSlider);
            }

            resolve();
          } catch (e) {
            console.error('Error mounting Splide:', e);
            resolve();
          }
        });
      })
    }

    /**
     * Initialize lazy loading for thumbnail images with LQIP
     * Shows blurred low-res image, then transitions to sharp high-res
     */
    initThumbLazyLoad() {
      const thumbImages = this.thumbSlider.querySelectorAll('img.lazyload[data-src]');

      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              const highResSrc = img.dataset.src;

              // Create new image to preload high-res version
              const preloadImg = new Image();
              preloadImg.onload = () => {
                img.src = highResSrc;
                img.classList.remove('lazyload');
                img.classList.add('lazyloaded');
              };
              preloadImg.src = highResSrc;

              observer.unobserve(img);
            }
          });
        }, {
          rootMargin: '50px' // Start loading slightly before they come into view
        });

        thumbImages.forEach(img => imageObserver.observe(img));
      } else {
        // Fallback for browsers without IntersectionObserver
        thumbImages.forEach(img => {
          img.src = img.dataset.src;
          img.classList.remove('lazyload');
          img.classList.add('lazyloaded');
        });
      }
    }

    /**
     * Initialize LQIP for main slider images
     * Since Splide's lazy loading is disabled, we handle it ourselves
     * @param {number} startIndex - The starting slide index to load
     */
    initMainSliderLQIP(startIndex = 0) {
      if (!this.mainSplide) return;

      // Load images when slides change
      this.mainSplide.on('moved', (newIndex) => {
        this.ensureSlideImagesLoaded(newIndex);
      });

      // Load initial slide images using the provided startIndex
      // This ensures we load the correct slide even if Splide.index hasn't updated yet
      this.ensureSlideImagesLoaded(startIndex);
    }

    /**
     * Get slide element by index, accounting for loop mode clones
     * @param {number} index - Splide slide index
     * @returns {HTMLElement|null} The slide element
     */
    getSlideElement(index) {
      if (!this.mainSplide || !this.mainSplide.Components || !this.mainSplide.Components.Slides) {
        return null;
      }

      const slide = this.mainSplide.Components.Slides.getAt(index);
      return slide ? slide.slide : null;
    }

    /**
     * Load high-res images for slides near the current index
     * Uses LQIP pattern: shows blurred low-res first, then swaps to high-res
     */
    ensureSlideImagesLoaded(slideIndex) {
      if (!this.mainSplide || !this.mainSplide.Components || !this.mainSplide.Components.Slides) {
        return;
      }

      // Get total number of slides (excluding clones)
      const slideCount = this.mainSplide.length;

      // Load current slide and neighbors
      const indicesToLoad = [slideIndex - 1, slideIndex, slideIndex + 1].filter(
        i => i >= 0 && i < slideCount
      );

      indicesToLoad.forEach(idx => {
        // Use Splide's API to get the correct slide element
        const slide = this.getSlideElement(idx);
        if (!slide) return;

        // Find all images in the slide (handles both regular images and Vimeo placeholders)
        const images = slide.querySelectorAll('img[data-splide-lazy]');
        if (images.length === 0) return;

        images.forEach(img => {
          const lazySrc = img.dataset.splideLazy;
          const currentSrc = img.getAttribute('src');

          // Skip if already loaded high-res
          if (currentSrc && currentSrc.includes('width=1400')) {
            if (!img.classList.contains('lazyloaded')) {
              img.classList.add('lazyloaded');
            }
            return;
          }

          // Need to load high-res
          if (lazySrc) {
            // Preload and swap to high-res
            const preloadImg = new Image();
            preloadImg.onload = () => {
              img.src = lazySrc;
              img.removeAttribute('data-splide-lazy');
              img.classList.add('lazyloaded');
            };
            preloadImg.onerror = () => {
              console.warn('Failed to load image:', lazySrc);
            };
            preloadImg.src = lazySrc;
          }
        });
      });
    }

    /**
     * Ensure main-lqip class is on images that haven't loaded yet
     * Since we disabled Splide lazy loading, images keep their original attributes
     */
    normalizeSlideImages(container) {
      const images = container.querySelectorAll('img');
      images.forEach((img) => {
        const currentSrc = img.getAttribute('src');
        const lazySrc = img.dataset.splideLazy;

        // If has LQIP src and data-splide-lazy, ensure blur class
        if (currentSrc && lazySrc && !currentSrc.includes('width=1400')) {
          img.classList.add('main-lqip');
        }
        // If already has high-res, mark as loaded
        else if (currentSrc && currentSrc.includes('width=1400')) {
          img.classList.add('lazyloaded');
        }
      });
    }

    /**
     * Setup keyboard navigation for thumbnail slider
     * Handles arrow keys (all directions) and Enter/Space for activation
     */
    setupThumbnailKeyboardNav() {
      if (!this.thumbSlider) return;

      this.thumbSlider.addEventListener('keydown', (e) => {
        // Only handle when a thumbnail slide is focused
        const focusedSlide = document.activeElement;
        if (!focusedSlide || !focusedSlide.classList.contains('splide__slide')) return;
        if (!this.thumbSlider.contains(focusedSlide)) return;

        const currentIndex = this.thumbSplide?.index ?? 0;
        const slideCount = this.thumbSplide?.length ?? 0;

        switch (e.key) {
          case 'ArrowUp':
          case 'ArrowLeft':
            e.preventDefault();
            if (currentIndex > 0) {
              this.thumbSplide.go(currentIndex - 1);
              this.focusThumbnailAtIndex(currentIndex - 1);
            }
            break;

          case 'ArrowDown':
          case 'ArrowRight':
            e.preventDefault();
            if (currentIndex < slideCount - 1) {
              this.thumbSplide.go(currentIndex + 1);
              this.focusThumbnailAtIndex(currentIndex + 1);
            }
            break;

          case 'Home':
            e.preventDefault();
            this.thumbSplide.go(0);
            this.focusThumbnailAtIndex(0);
            break;

          case 'End':
            e.preventDefault();
            this.thumbSplide.go(slideCount - 1);
            this.focusThumbnailAtIndex(slideCount - 1);
            break;

          case 'Enter':
          case ' ':
            // Splide's isNavigation already handles click on Enter/Space
            // but we ensure focus stays on the thumbnail
            e.preventDefault();
            this.thumbSplide.go(currentIndex);
            break;
        }
      });
    }

    /**
     * Focus the thumbnail at the given index
     */
    focusThumbnailAtIndex(index) {
      const slides = this.thumbSlider.querySelectorAll('.splide__slide');
      if (slides[index]) {
        slides[index].focus();
      }
    }

    /**
     * Setup keyboard navigation for main slider
     * Allows arrow key navigation when main slider or its slides are focused
     */
    setupMainSliderKeyboardNav() {
      if (!this.mainSlider) return;

      // Make main slider focusable
      this.mainSlider.setAttribute('tabindex', '0');
      this.mainSlider.setAttribute('role', 'region');
      this.mainSlider.setAttribute('aria-label', 'Product image gallery');

      this.mainSlider.addEventListener('keydown', (e) => {
        if (!this.mainSplide) return;

        const currentIndex = this.mainSplide.index ?? 0;
        const slideCount = this.mainSplide.length ?? 0;

        switch (e.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            this.mainSplide.go('<');
            break;

          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            this.mainSplide.go('>');
            break;

          case 'Home':
            e.preventDefault();
            this.mainSplide.go(0);
            break;

          case 'End':
            e.preventDefault();
            this.mainSplide.go(slideCount - 1);
            break;

          case 'Enter':
          case ' ':
            // Open fullscreen on Enter/Space
            e.preventDefault();
            this.openFullscreen();
            break;
        }
      });
    }

    /**
     * Match thumbnail slider height to main slider using CSS custom property
     * Uses CSS variable for faster updates with less reflow
     */
    matchThumbnailHeight() {
      if (window.innerWidth > 1100) {
        const mainHeight = this.mainSlider.offsetHeight;
        if (mainHeight) {
          // Set CSS custom property - faster than setting style.height directly
          this.wrapper.style.setProperty('--gallery-main-height', mainHeight + 'px');
        }
      } else {
        // Remove property on mobile to let CSS handle it
        this.wrapper.style.removeProperty('--gallery-main-height');
      }

      // Re-check if thumb slider should be disabled after height change
      this.checkAndDisableThumbSlider();
    }

    /**
     * Check if all thumbnails fit within the container
     * If they do, disable the Splide slider (no arrows, no drag, no sliding)
     * CSS handles the visual lockdown via .splide--disabled class
     */
    checkAndDisableThumbSlider() {
      if (!this.thumbSlider || !this.thumbSplide) return;

      const isDesktop = window.innerWidth > 1100;
      const slides = this.thumbSlider.querySelectorAll('.splide__slide');
      const slideCount = slides.length;

      if (slideCount === 0) return;

      // Get the track element
      const track = this.thumbSlider.querySelector('.splide__track');
      if (!track) return;

      let allThumbsFit = false;

      if (isDesktop) {
        // Vertical layout - check if total height of thumbs fits in container
        const thumbHeight = 84; // Fixed height per thumb
        const gap = 6; // Gap between thumbs
        const totalThumbsHeight = (slideCount * thumbHeight) + ((slideCount - 1) * gap);
        const containerHeight = track.offsetHeight || parseInt(getComputedStyle(track).height, 10);

        allThumbsFit = totalThumbsHeight <= containerHeight;
      } else {
        // Horizontal layout - check if all thumbs fit
        // At mobile breakpoint, perPage is 7, so if we have 7 or fewer slides, they all fit
        allThumbsFit = slideCount <= 7;
      }

      if (allThumbsFit) {
        // Add class - CSS handles hiding arrows and locking transform
        this.thumbSlider.classList.add('splide--disabled');
      } else {
        this.thumbSlider.classList.remove('splide--disabled');
      }
    }

    /**
     * Debounce helper
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    /**
     * Derive variant configuration (variant ID and featured media)
     */
    _deriveVariantConfig(variant) {

      if (!variant) {
        return { variantId: null, featuredMediaId: null };
      }

      const variantId = String(variant.id);
      const featuredMediaId = variant.featured_media?.id || null;

      return { variantId, featuredMediaId };
    }

    /**
     * Render gallery for a specific variant
     * This is the public API called by theme.js
     * Optimized to not block the main thread with requestAnimationFrame
     * Includes fade transition for smooth variant switching
     */
    renderVariant(variant) {
      // Cancel any pending variant update
      if (this._pendingVariantUpdate) {
        cancelAnimationFrame(this._pendingVariantUpdate);
        this._pendingVariantUpdate = null;
      }

      const config = this._deriveVariantConfig(variant);

      // If no variant media mapping exists, just navigate to featured media without rebuilding
      if (this.variantMediaMap.size === 0) {
        if (config.featuredMediaId && this.mainSplide) {
          // Find the slide with this media ID
          const targetIndex = this.mainCache.findIndex(item =>
            item.mediaId === String(config.featuredMediaId)
          );

          if (targetIndex !== -1 && this.mainSplide) {
            this.mainSplide.go(targetIndex);

            // Also update thumbnail slider to ensure it's in sync
            if (this.thumbSplide) {
              this.thumbSplide.go(targetIndex);
            }
          }
        }
        return;
      }

      // Check if this is actually a new image set (different variant with different images)
      const isNewImageSet = String(config.variantId) !== String(this.currentVariantId);

      // Schedule DOM work in next animation frame to avoid blocking
      this._pendingVariantUpdate = requestAnimationFrame(() => {
        this._pendingVariantUpdate = null;

        // If this is a new image set, add fade transition
        if (isNewImageSet) {
          // Start fade-out
          this.mainSlider.classList.add('is-transitioning');

          // Wait for fade-out animation (300ms)
          setTimeout(() => {
            // Pause and destroy videos before rebuilding
            this.destroyMainSliderVideos();

            // Build slides for the new variant
            const startIndex = this.buildSlides(config.variantId, config.featuredMediaId);

            // Mount Splide immediately
            this.mountSplide(startIndex).then(() => {
              this.currentVariantId = String(config.variantId);

              // Pre-load the starting slide's media before fading in to prevent
              // LQIP blur-to-sharp or video poster flash happening during fade-in
              const doFadeIn = () => {
                requestAnimationFrame(() => {
                  this.mainSlider.classList.remove('is-transitioning');
                  this.mainSlider.classList.add('is-visible');

                  // Clean up classes after fade-in completes
                  setTimeout(() => {
                    this.mainSlider.classList.remove('is-visible');
                  }, 300);
                });
              };

              // Defer fullscreen slider rebuild until idle (it's not currently visible)
              if (this.fullscreenSplide && this._pendingFullscreenSlides) {
                this._scheduleIdleTask(() => {
                  if (this.fullscreenSlider && this._pendingFullscreenSlides) {
                    const fullscreenList = this.fullscreenSlider.querySelector('.splide__list');
                    if (fullscreenList) {
                      const fragment = document.createDocumentFragment();
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = this._pendingFullscreenSlides.map(item => item.html).join('');
                      while (tempDiv.firstChild) {
                        fragment.appendChild(tempDiv.firstChild);
                      }
                      fullscreenList.innerHTML = '';
                      fullscreenList.appendChild(fragment);
                      this._pendingFullscreenSlides = null;

                      // Remount fullscreen slider
                      this.mountFullscreenSplide(startIndex);
                    }
                  }
                });
              }

              // Fade in only after the starting slide's media is ready, preventing
              // a visible LQIP blur-to-sharp or video poster flash during the fade-in
              const initialSlide = this.getSlideElement(startIndex);
              const lazyImg = initialSlide?.querySelector('img[data-splide-lazy]');
              const video = initialSlide?.querySelector('video[poster]');
              const preloadSrc = lazyImg?.dataset.splideLazy || video?.getAttribute('poster');

              if (preloadSrc) {
                const maxWait = setTimeout(doFadeIn, 250);
                const preload = new Image();
                preload.onload = preload.onerror = () => {
                  clearTimeout(maxWait);
                  doFadeIn();
                };
                preload.src = preloadSrc;
              } else {
                doFadeIn();
              }
            });
          }, 300);
        } else {
          // Same image set, just navigate without transition
          const startIndex = this.buildSlides(config.variantId, config.featuredMediaId);
          this.mountSplide(startIndex).then(() => {
            if (this.fullscreenSplide && this._pendingFullscreenSlides) {
              this._scheduleIdleTask(() => {
                if (this.fullscreenSlider && this._pendingFullscreenSlides) {
                  const fullscreenList = this.fullscreenSlider.querySelector('.splide__list');
                  if (fullscreenList) {
                    const fragment = document.createDocumentFragment();
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = this._pendingFullscreenSlides.map(item => item.html).join('');
                    while (tempDiv.firstChild) {
                      fragment.appendChild(tempDiv.firstChild);
                    }
                    fullscreenList.innerHTML = '';
                    fullscreenList.appendChild(fragment);
                    this._pendingFullscreenSlides = null;

                    // Remount fullscreen slider
                    this.mountFullscreenSplide(startIndex);
                  }
                }
              });
            }
          });
        }
      });
    }

    /**
     * Setup fullscreen gallery
     */
    setupFullscreenGallery() {
      if (!this.fullscreenModal || !this.fullscreenSlider) {
        console.warn('Fullscreen gallery elements not found');
        return;
      }

      // Open fullscreen on zoom button click
      if (this.zoomButton) {
        this.zoomButton.addEventListener('click', () => {
          this.openFullscreen();
        });
      }

      // Close fullscreen on close button click
      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.closeFullscreen();
        });
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.fullscreenModal && !this.fullscreenModal.hasAttribute('hidden')) {
          this.closeFullscreen();
        }
      });

      // Keyboard navigation for fullscreen
      document.addEventListener('keydown', (e) => {
        if (this.fullscreenModal && !this.fullscreenModal.hasAttribute('hidden') && this.fullscreenSplide) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            this.fullscreenSplide.go('>');
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            this.fullscreenSplide.go('<');
          }
        }
      });

      // Close on background click
      this.fullscreenModal.addEventListener('click', (e) => {
        if (e.target === this.fullscreenModal) {
          this.closeFullscreen();
        }
      });

    }

    /**
     * Open fullscreen gallery
     */
    openFullscreen() {
      if (!this.fullscreenModal || !this.fullscreenSlider) {
        console.warn('⚠️ Fullscreen elements not found');
        return;
      }

      // Get current index from main slider with fallbacks
      let currentIndex = 0;

      if (this.mainSplide && this.mainSplide.Components && this.mainSplide.Components.Slides && typeof this.mainSplide.index !== 'undefined') {
        currentIndex = this.mainSplide.index;
      } else if (typeof this._lastMainSliderIndex !== 'undefined') {
        currentIndex = this._lastMainSliderIndex;
      }


      // Pause all videos in main slider before opening fullscreen
      this.pauseMainSliderVideos();

      // Stop all media in the current main slider slide
      const currentSlide = this.mainSlider.querySelectorAll('[data-gallery-main-slide]')[currentIndex];
      if (this.mainSlider && currentIndex >= 0) {
        if (currentSlide) {
          this.stopMediaOnSlide(currentSlide);
        }
      }

      // If there are pending fullscreen slides, rebuild them now before opening
      // This handles both:
      // 1. Initial page load with a variant-specific URL parameter
      // 2. Variant changes that happened after page load
      if (this._pendingFullscreenSlides) {
        const fullscreenList = this.fullscreenSlider.querySelector('.splide__list');
        if (fullscreenList) {
          const fragment = document.createDocumentFragment();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this._pendingFullscreenSlides.map(item => item.html).join('');
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          fullscreenList.innerHTML = '';
          fullscreenList.appendChild(fragment);
          this._pendingFullscreenSlides = null;
        }
      }

      // Always destroy and remount to ensure correct starting position
      this.mountFullscreenSplide(currentIndex);

      // Show modal
      this.fullscreenModal.removeAttribute('hidden');
      setTimeout(() => {
        this.fullscreenModal.classList.add('is-active');
        document.body.classList.add('has-full-screen-modal-active');

        // Focus the close button for keyboard accessibility
        if (this.closeButton) {
          this.closeButton.focus();
        }
      }, 10);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Store the element that had focus before opening
      this._previouslyFocusedElement = document.activeElement;

    }

    /**
     * Close fullscreen gallery
     */
    closeFullscreen() {
      if (!this.fullscreenModal) return;

      // Get the current fullscreen index before closing
      const fullscreenIndex = this.fullscreenSplide?.index ?? 0;

      // Sync main and thumbnail sliders to the fullscreen position
      if (this.mainSplide && typeof fullscreenIndex === 'number') {
        this.mainSplide.go(fullscreenIndex);
      }
      if (this.thumbSplide && typeof fullscreenIndex === 'number') {
        this.thumbSplide.go(fullscreenIndex);
      }

      // Pause any playing videos
      this.pauseAllVideos();

      // Hide modal
      this.fullscreenModal.classList.remove('is-active');
      document.body.classList.remove('has-full-screen-modal-active');
      setTimeout(() => {
        this.fullscreenModal.setAttribute('hidden', '');
      }, 300);

      // Restore body scroll
      document.body.style.overflow = '';

      // Restore focus to previously focused element or main slider
      if (this._previouslyFocusedElement && typeof this._previouslyFocusedElement.focus === 'function') {
        this._previouslyFocusedElement.focus();
      } else if (this.mainSlider) {
        this.mainSlider.focus();
      }

    }

    /**
     * Mount fullscreen Splide slider
     */
    mountFullscreenSplide(startIndex = 0) {
      if (!this.fullscreenSlider) {
        console.warn('⚠️ Fullscreen slider element not found');
        return;
      }

      // Check if there are slides to display
      const slideCount = this.fullscreenSlider.querySelectorAll('.splide__slide').length;
      if (slideCount === 0) {
        console.warn('⚠️ No slides found in fullscreen slider');
        return;
      }

      // Destroy existing instance
      if (this.fullscreenSplide) {
        try {
          this.fullscreenSplide.destroy();
        } catch (e) {
          console.warn('Error destroying fullscreen splide:', e);
        }
        this.fullscreenSplide = null;
      }

      try {

        // Initialize fullscreen slider with vertical direction
        this.fullscreenSplide = new Splide(this.fullscreenSlider, {
          direction: 'ttb',
          height: '100vh',
          wheel: true,
          wheelMinThreshold: 50,
          wheelSleep: 500,
          dragMinThreshold: {
            mouse: 10,
            touch: 10,
          },
          flickPower: 300,
          flickMaxPages: 1,
          lazyLoad: 'nearby',
          releaseWheel: true,
          rewind: false,
          pagination: false,
          start: startIndex,
          arrows: false,
          speed: 600,
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          updateOnMove: true
        });

        // Track previous index to detect actual slide changes
        let previousFullscreenIndex = startIndex;

        this.fullscreenSplide.on('moved', (newIndex) => {
          // Only pause videos if we actually changed slides
          if (newIndex !== previousFullscreenIndex) {
            this.pauseAllVideos();
            previousFullscreenIndex = newIndex;
          }
        });

        // Setup after mount
        this.fullscreenSplide.on('mounted', () => {
          this.fullscreenSlider.dispatchEvent(new CustomEvent('gallery:fullscreenMounted', {
            detail: {
              splide: this.fullscreenSplide
            }
          }));

          this.setupVideoPlayers();

          // Refresh to recalculate positions for vertical layout, then navigate
          requestAnimationFrame(() => {
            if (!this.fullscreenSplide || !this.fullscreenSplide.Components || !this.fullscreenSplide.Components.Slides) {
              console.error('❌ Splide Components not fully available after mount');
              return;
            }

            this.fullscreenSplide.refresh();

            requestAnimationFrame(() => {

              // Temporarily disable animation for initial navigation
              const originalSpeed = this.fullscreenSplide.options.speed;
              this.fullscreenSplide.options.speed = 0;

              this.fullscreenSplide.go(startIndex);

              // Restore animation speed
              this.fullscreenSplide.options.speed = originalSpeed;

            });
          });
        });

        this.fullscreenSplide.mount();

      } catch (e) {
        console.error('❌ Error mounting fullscreen Splide:', e);
      }
    }

    /**
     * Setup video players
     */
    setupVideoPlayers() {
      const videoSlides = this.fullscreenSlider.querySelectorAll('[data-media-type="external_video"]');

      videoSlides.forEach((slide) => {
        const playButton = slide.querySelector('[data-video-play]');
        const videoContainer = slide.querySelector('[data-video-container]');
        const placeholder = slide.querySelector('[data-video-placeholder]');
        const iframeWrapper = slide.querySelector('[data-video-iframe]');

        if (!playButton || !videoContainer) return;

        playButton.addEventListener('click', () => {
          const videoUrl = slide.dataset.videoUrl;
          const videoHost = slide.dataset.videoHost;

          if (!videoUrl) return;

          // Build iframe based on host
          let iframeSrc = '';

          if (videoHost === 'vimeo') {
            iframeSrc = `https://player.vimeo.com/video/${videoUrl}?autoplay=1&title=0&byline=0&portrait=0`;
          } else if (videoHost === 'youtube') {
            iframeSrc = `https://www.youtube.com/embed/${videoUrl}?autoplay=1&rel=0`;
          }

          if (iframeSrc && iframeWrapper) {
            // Create iframe
            const iframe = document.createElement('iframe');
            iframe.src = iframeSrc;
            iframe.allow = 'autoplay; fullscreen; picture-in-picture';
            iframe.allowFullscreen = true;

            // Clear and append iframe
            iframeWrapper.innerHTML = '';
            iframeWrapper.appendChild(iframe);

            // Show iframe, hide placeholder
            placeholder.style.display = 'none';
            iframeWrapper.removeAttribute('hidden');

          }
        });
      });

      // Setup Vimeo videos in fullscreen
      this.setupFullscreenVimeoHandlers();
    }

    /**
     * Setup listener to disable slider dragging when Vimeo player controls are being used
     * This prevents the slider from moving when the user is interacting with video controls
     */
    setupVimeoControlInteraction() {
      // Listen for Vimeo player control interaction events
      this.wrapper.addEventListener('vimeoPlayer:controlStart', () => {
        // Disable Splide drag while controls are being used
        if (this.mainSplide && this.mainSplide.Components && this.mainSplide.Components.Drag) {
          this.mainSplide.Components.Drag.disable(true);
        }
      });

      this.wrapper.addEventListener('vimeoPlayer:controlEnd', () => {
        // Re-enable Splide drag after control interaction ends
        if (this.mainSplide && this.mainSplide.Components && this.mainSplide.Components.Drag) {
          this.mainSplide.Components.Drag.disable(false);
        }
      });

      // Also listen on fullscreen slider if it exists
      if (this.fullscreenSlider) {
        this.fullscreenSlider.addEventListener('vimeoPlayer:controlStart', () => {
          if (this.fullscreenSplide && this.fullscreenSplide.Components && this.fullscreenSplide.Components.Drag) {
            this.fullscreenSplide.Components.Drag.disable(true);
          }
        });

        this.fullscreenSlider.addEventListener('vimeoPlayer:controlEnd', () => {
          if (this.fullscreenSplide && this.fullscreenSplide.Components && this.fullscreenSplide.Components.Drag) {
            this.fullscreenSplide.Components.Drag.disable(false);
          }
        });
      }
    }

    /**
     * Setup Vimeo video handlers for main slider
     * Uses VimeoPlayerComponent for full controls
     */
    setupMainSliderVimeoHandlers() {
      const vimeoSlides = this.mainSlider.querySelectorAll('[data-is-vimeo="true"]');

      vimeoSlides.forEach((slide) => {
        // Skip if already initialized
        if (slide.dataset.vimeoInitialized === 'true') return;

        const placeholder = slide.querySelector('[data-vimeo-placeholder]');
        const vimeoContainer = slide.querySelector('[data-vimeo-container]');

        if (!placeholder || !vimeoContainer) return;

        // Click handler on the placeholder to initialize the Vimeo player
        placeholder.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const vimeoId = slide.dataset.videoVimeoId;
          if (!vimeoId) return;

          // Check if already has a player
          const playerKey = `main-${slide.dataset.mediaId}`;
          if (this.vimeoPlayers.has(playerKey)) {
            // Player exists, just play it
            const existingPlayer = this.vimeoPlayers.get(playerKey);
            if (existingPlayer && typeof existingPlayer.play === 'function') {
              existingPlayer.play();
              placeholder.classList.add('is-hidden');
              vimeoContainer.classList.add('is-active');
            }
            return;
          }

          // Check if VimeoPlayerComponent is available
          if (typeof window.VimeoPlayerComponent === 'undefined') {
            console.warn('VimeoPlayerComponent not loaded');
            return;
          }

          // Get poster image URL
          const posterImg = placeholder.querySelector('img');
          const posterUrl = posterImg ? posterImg.src || posterImg.dataset.src || posterImg.dataset.splideLazy : null;

          // Create player wrapper and append (don't replace innerHTML to preserve placeholder)
          let playerContainer = vimeoContainer.querySelector('[data-vimeo-player]');
          if (!playerContainer) {
            const playerWrapper = document.createElement('div');
            playerWrapper.className = 'vimeo-player-wrapper';
            playerWrapper.setAttribute('data-vimeo-player', '');
            vimeoContainer.appendChild(playerWrapper);
            playerContainer = playerWrapper;
          }

          // Initialize VimeoPlayerComponent
          const player = new window.VimeoPlayerComponent(playerContainer, {
            vimeoId: vimeoId,
            autoplay: true,
            muted: false,
            showControls: true,
            posterUrl: null // Don't show poster since we're autoplaying
          });

          this.vimeoPlayers.set(playerKey, player);

          // Hide placeholder with smooth transition
          placeholder.classList.add('is-hidden');
          vimeoContainer.classList.add('is-active');

          // Mark slide as initialized
          slide.dataset.vimeoInitialized = 'true';

        });

        // Allow slider drag to work on video container by not stopping propagation on drag events
        vimeoContainer.addEventListener('touchstart', (e) => {
          // Only prevent default if it's a tap (not a drag)
          this._touchStartX = e.touches[0].clientX;
          this._touchStartY = e.touches[0].clientY;
        }, { passive: true });

        vimeoContainer.addEventListener('touchmove', (e) => {
          // Allow the event to propagate for slider drag
        }, { passive: true });
      });
    }

    /**
     * Setup Vimeo video handlers for fullscreen gallery
     * Uses VimeoPlayerComponent for full controls
     */
    setupFullscreenVimeoHandlers() {
      if (!this.fullscreenSlider) return;

      const vimeoSlides = this.fullscreenSlider.querySelectorAll('[data-is-vimeo="true"]');

      vimeoSlides.forEach((slide) => {
        // Skip if already initialized
        if (slide.dataset.vimeoInitialized === 'true') return;

        const placeholder = slide.querySelector('[data-vimeo-placeholder]');
        const vimeoContainer = slide.querySelector('[data-vimeo-container]');

        if (!placeholder || !vimeoContainer) return;

        // Click handler on the placeholder to initialize the Vimeo player
        const initPlayer = () => {
          const vimeoId = slide.dataset.videoVimeoId;
          if (!vimeoId) return;

          const playerKey = `fullscreen-${slide.dataset.mediaId}`;

          // Check if player already exists
          if (this.vimeoPlayers.has(playerKey)) {
            // Player exists, just play it
            const existingPlayer = this.vimeoPlayers.get(playerKey);
            if (existingPlayer && typeof existingPlayer.play === 'function') {
              existingPlayer.play();
              placeholder.classList.add('is-hidden');
              vimeoContainer.classList.add('is-active');
            }
            return;
          }

          // Check if VimeoPlayerComponent is available
          if (typeof window.VimeoPlayerComponent === 'undefined') {
            console.warn('VimeoPlayerComponent not loaded');
            return;
          }

          // Get poster image URL
          const posterImg = placeholder.querySelector('img');
          const posterUrl = posterImg ? posterImg.src || posterImg.dataset.src || posterImg.dataset.splideLazy : null;

          // Create player wrapper and append (don't replace innerHTML to preserve placeholder)
          let playerContainer = vimeoContainer.querySelector('[data-vimeo-player]');
          if (!playerContainer) {
            const playerWrapper = document.createElement('div');
            playerWrapper.className = 'vimeo-player-wrapper vimeo-player-wrapper--fullscreen';
            playerWrapper.setAttribute('data-vimeo-player', '');
            vimeoContainer.appendChild(playerWrapper);
            playerContainer = playerWrapper;
          }

          // Initialize VimeoPlayerComponent
          const player = new window.VimeoPlayerComponent(playerContainer, {
            vimeoId: vimeoId,
            autoplay: true,
            muted: false,
            showControls: true,
            posterUrl: null // Don't show poster since we're autoplaying
          });

          this.vimeoPlayers.set(playerKey, player);

          // Hide placeholder with transition
          placeholder.classList.add('is-hidden');
          vimeoContainer.classList.add('is-active');

          // Mark slide as initialized
          slide.dataset.vimeoInitialized = 'true';

        };

        // Store init function on slide for auto-start
        slide._initPlayer = initPlayer;

        placeholder.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          initPlayer();
        });

        // Allow slider drag to work on video container
        vimeoContainer.addEventListener('touchstart', (e) => {
          this._touchStartX = e.touches[0].clientX;
          this._touchStartY = e.touches[0].clientY;
        }, { passive: true });

        vimeoContainer.addEventListener('touchmove', (e) => {
          // Allow the event to propagate for slider drag
        }, { passive: true });
      });
    }

    /**
     * Setup video fullscreen modal
     */
    setupVideoFullscreenModal() {
      if (!this.videoFullscreenModal || !this.videoFullscreenClose) {
        console.warn('Video fullscreen modal elements not found');
        return;
      }

      // Close button
      this.videoFullscreenClose.addEventListener('click', () => {
        this.closeVideoFullscreen();
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.videoFullscreenModal && !this.videoFullscreenModal.hasAttribute('hidden')) {
          this.closeVideoFullscreen();
        }
      });
    }

    /**
     * Open video in fullscreen modal
     */
    openVideoFullscreen(vimeoId) {
      if (!this.videoFullscreenModal || !this.videoFullscreenContent) return;

      // Build Vimeo iframe with proper autoplay parameters and captions
      // Note: Remove autoplay=1 from URL, we'll use Player API to play
      const iframeSrc = `https://player.vimeo.com/video/${vimeoId}?muted=0&title=0&byline=0&portrait=0&background=0&texttrack=en`;

      // Create iframe
      const iframe = document.createElement('iframe');
      iframe.src = iframeSrc;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.setAttribute('frameborder', '0');
      iframe.id = 'vimeo-player-modal-' + vimeoId;
      iframe.width = '100%';
      iframe.height = '100%';

      // Clear content and append iframe
      this.videoFullscreenContent.innerHTML = '';
      this.videoFullscreenContent.appendChild(iframe);

      // Show modal
      this.videoFullscreenModal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      // Use Vimeo Player API to play the video
      // Wait for iframe to load, then play
      iframe.onload = () => {
        try {
          // Use postMessage API to play the video
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
        } catch (err) {
          console.warn('Could not autoplay Vimeo video:', err);
        }
      };

    }

    /**
     * Close video fullscreen modal
     */
    closeVideoFullscreen() {
      if (!this.videoFullscreenModal || !this.videoFullscreenContent) return;

      // Clear iframe
      this.videoFullscreenContent.innerHTML = '';

      // Hide video modal
      this.videoFullscreenModal.setAttribute('hidden', '');

      // Also close the gallery fullscreen modal
      this.closeFullscreen();

    }

    /**
     * Reset all videos in fullscreen gallery to poster state
     */
    pauseAllVideos() {
      if (!this.fullscreenSlider) return;

      // Destroy VimeoPlayerComponent instances for fullscreen and reset to poster
      this.vimeoPlayers.forEach((player, key) => {
        if (key.startsWith('fullscreen-')) {
          if (player && typeof player.destroy === 'function') {
            player.destroy();
          }
          this.vimeoPlayers.delete(key);
        }
      });

      // Reset all Vimeo slides in fullscreen to poster state
      const vimeoSlides = this.fullscreenSlider.querySelectorAll('[data-is-vimeo="true"]');
      vimeoSlides.forEach((slide) => {
        const placeholder = slide.querySelector('[data-vimeo-placeholder]');
        const vimeoContainer = slide.querySelector('[data-vimeo-container]');

        if (placeholder) {
          placeholder.classList.remove('is-hidden');
        }
        if (vimeoContainer) {
          vimeoContainer.classList.remove('is-active');
          const playerWrapper = vimeoContainer.querySelector('.vimeo-player-wrapper');
          if (playerWrapper) {
            playerWrapper.remove();
          }
        }

        // Reset initialized flag
        slide.dataset.vimeoInitialized = 'false';
      });

      // Pause native videos in fullscreen
      const videos = this.fullscreenSlider.querySelectorAll('video');
      videos.forEach((video) => {
        video.pause();
      });

      // Also reset videos in main slider
      this.pauseMainSliderVideos();
    }

    /**
     * Reset video on a specific slide to initial state (poster image)
     */
    resetVideoOnSlide(slide) {
      if (!slide) return;

      const mediaId = slide.dataset.mediaId;
      const playerKey = `main-${mediaId}`;

      // Destroy VimeoPlayerComponent if exists
      if (this.vimeoPlayers.has(playerKey)) {
        const player = this.vimeoPlayers.get(playerKey);
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
        this.vimeoPlayers.delete(playerKey);
      }

      // Reset video slide to initial state (show poster)
      if (slide.dataset.isVimeo === 'true') {
        const placeholder = slide.querySelector('[data-vimeo-placeholder]');
        const vimeoContainer = slide.querySelector('[data-vimeo-container]');

        if (placeholder) {
          placeholder.classList.remove('is-hidden');
        }
        if (vimeoContainer) {
          vimeoContainer.classList.remove('is-active');
          const playerWrapper = vimeoContainer.querySelector('.vimeo-player-wrapper');
          if (playerWrapper) {
            playerWrapper.remove();
          }
        }

        // Reset initialized flag so it can be clicked again
        slide.dataset.vimeoInitialized = 'false';
      }

      // Pause and reset any native video elements
      const videos = slide.querySelectorAll('video');
      videos.forEach(video => { video.pause(); video.currentTime = 0; });

    }

    /**
     * Pause all videos in main slider (without destroying - used for fullscreen open)
     */
    pauseMainSliderVideos() {
      if (!this.mainSlider) return;

      // Pause all VimeoPlayerComponent instances for main slider
      this.vimeoPlayers.forEach((player, key) => {
        if (key.startsWith('main-') && player && typeof player.pause === 'function') {
          player.pause();
        }
      });

      // Pause native videos
      const videos = this.mainSlider.querySelectorAll('video');
      videos.forEach(video => video.pause());
    }

    /**
     * Destroy all videos in main slider (used when rebuilding slides for variant change)
     */
    destroyMainSliderVideos() {
      if (!this.mainSlider) return;

      // Destroy VimeoPlayerComponent instances for main slider
      this.vimeoPlayers.forEach((player, key) => {
        if (key.startsWith('main-')) {
          player.destroy();
          this.vimeoPlayers.delete(key);
        }
      });

      // Reset Vimeo videos in main slider
      const vimeoSlides = this.mainSlider.querySelectorAll('[data-is-vimeo="true"]');
      vimeoSlides.forEach((slide) => {
        const placeholder = slide.querySelector('[data-vimeo-placeholder]');
        const vimeoContainer = slide.querySelector('[data-vimeo-container]');
        const iframeWrapper = slide.querySelector('[data-vimeo-iframe]');

        if (placeholder) {
          placeholder.classList.remove('is-hidden');
        }
        if (vimeoContainer) {
          vimeoContainer.classList.remove('is-active');
          // Clear any VimeoPlayerComponent content
          const playerWrapper = vimeoContainer.querySelector('.vimeo-player-wrapper');
          if (playerWrapper) {
            playerWrapper.remove();
          }
        }
        if (iframeWrapper) {
          iframeWrapper.classList.remove('is-visible');
          iframeWrapper.setAttribute('hidden', '');
          iframeWrapper.innerHTML = '';
        }
      });
    }

    /**
     * Stop media on a slide (videos, models, etc.)
     * Note: Videos are handled by VimeoPlayerComponent - this only handles 3D models
     */
    stopMediaOnSlide(slide) {
      if (!slide) return;

      // Stop 3D models
      const currentMedia = slide.querySelector('[data-product-media-type-model]');
      if (currentMedia) {
        currentMedia.dispatchEvent(
          new CustomEvent('mediaHidden', {
            bubbles: true,
            cancelable: true
          })
        );
      }
    }

    /**
     * Prepare media on a slide (play videos, show models, etc.)
     * Note: Vimeo videos are handled by VimeoPlayerComponent - this handles 3D models and Shopify-hosted videos
     */
    prepMediaOnSlide(slide) {
      if (!slide) return;

      // Autoplay Shopify-hosted video muted
      const shopifyVideo = slide.querySelector('[data-shopify-video]');
      if (shopifyVideo) {
        shopifyVideo.muted = true;
        shopifyVideo.play().catch(() => { });
      }

      // Show 3D models
      const nextMedia = slide.querySelector('[data-product-media-type-model]');
      if (nextMedia) {
        nextMedia.dispatchEvent(
          new CustomEvent('mediaVisible', {
            bubbles: true,
            cancelable: true
          })
        );
        const button = slide.querySelector('.shopify-model-viewer-ui__button');
        const closeBtn = slide.querySelector('.product-single__close-media');
        if (button) button.setAttribute('tabindex', 0);
        if (closeBtn) closeBtn.setAttribute('tabindex', 0);
      }
    }

    /**
     * Prevent Slick from initializing on Splide elements
     */
    preventSlick() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Check if Slick was added
              if (node.classList && node.classList.contains('slick-initialized')) {
                console.warn('Slick detected on Splide element, removing...');
                if (typeof jQuery !== 'undefined' && jQuery(node).slick) {
                  jQuery(node).slick('unslick');
                }
              }
            }
          });
        });
      });

      observer.observe(this.wrapper, {
        childList: true,
        subtree: true
      });
    }

    /**
     * Find the initial starting index based on data-starting-slide attribute
     */
    getInitialStartIndex() {
      const startingSlide = this.mainSlider.querySelector('[data-starting-slide]');
      if (!startingSlide) return 0;

      const allSlides = Array.from(this.mainSlider.querySelectorAll('[data-gallery-main-slide]'));
      return allSlides.indexOf(startingSlide);
    }
  }

  // Auto-initialize on DOM ready
  function initGalleries() {
    const wrappers = document.querySelectorAll('[data-product-gallery]');

    wrappers.forEach(wrapper => {
      // Skip if already initialized
      if (wrapper._productGalleryInstance) {
        return;
      }

      new ProductGallery(wrapper);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }

  // Expose class globally
  window.ProductGallery = ProductGallery;

})();
